/** @jsx h */
import { h } from 'preact'
import InputDate from './input-date'
import InputTime from './input-time'
import Select from './select'
import Textarea from './textarea'
import Button from './button'
import Step from './step'
import { useGlobal } from '../use-global'
import { useLabel } from '../use-label'
import useAnalytics from '../use-analytics'
import { useEffect, useState, useCallback } from 'preact/compat'
import { getBoutiqueAvailabilities, getTimeSlotsByDateAndType, getSpokenLanguages } from '../api'
import InputCheckboxRadio from './input-checkbox-radio'
import { useForm, Controller } from 'react-hook-form'
import dayjs from 'dayjs'
import clsx from 'clsx'
import { EXTERNAL_POINT_OF_SALE } from '../utils'

const CardStepDetails = ({ isOpen, isDone, onToggle, isEnabled, isHighlighted, conf, selectorClassName }) => {
  const t = useLabel()
  const analytics = useAnalytics()
  const [
    {
      hasSlotTakenError,
      selectedBoutique: {
        boutiqueType,
        id,
        boutiqueLanguage,
        communicationChannels: comChannels,
        openingHours,
        underLockdown,
        isVirtualEnabled
      },
      selectedPurpose: { enabledForVirtual },
      selectedCountry,
      appointmentDetails,
      selectedPurpose,
      canvasConfig
    },
    dispatch
  ] = useGlobal()

  const [availabilities, setAvailabilities] = useState([])
  const [spokenLanguages, setSpokenLanguages] = useState([])
  const [communicationChannels, setCommunicationChannels] = useState([])
  const [slots, setSlots] = useState([])
  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    register,
    watch
  } = useForm({
    defaultValues: {
      AD_Appointment_Type: underLockdown ? 'VIRTUAL' : 'INSTORE'
    }
  })
  const isExternalPointOfSale = boutiqueType === EXTERNAL_POINT_OF_SALE
  const watchADDate = watch('AD_Date')
  watch('AD_Appointment_Type')
  const [preferredTimes, setPreferredTimes] = useState([])

  const fetchSlots = useCallback(
    date => {
      getTimeSlotsByDateAndType({
        apiBaseUrl: canvasConfig.apiBaseUrl,
        date,
        boutiqueId: id,
        type: selectedPurpose.assetType
      }).then(data => {
        setSlots(data)
      })
    },
    [canvasConfig.apiBaseUrl, id, selectedPurpose.assetType]
  )

  useEffect(() => {
    if (!watchADDate) return

    const weekDay = dayjs(watchADDate)?.get('d')
    const dayOpenHours = { ...openingHours[weekDay] }
    const openHours = +dayOpenHours?.startT?.substring(0, 2) || 9
    const closingHours = +dayOpenHours?.endT?.substring(0, 2) || 18

    const options = []

    if (openHours <= 11) {
      options.push({
        value: 'Morning',
        label: t('Appointment_Details.AD_Morning')
      })
    }

    if (openHours <= 13 && closingHours >= 12) {
      options.push({
        value: 'Midday',
        label: t('Appointment_Details.AD_Midday')
      })
    }

    if (closingHours > 14) {
      options.push({
        value: 'Afternoon',
        label: t('Appointment_Details.AD_Afternoon')
      })
    }

    if (options.length === 1) {
      setValue('AD_Preferred_Time', options[0].value)
    }

    setPreferredTimes(options)

    return options
  }, [watchADDate])

  useEffect(() => {
    if (hasSlotTakenError) {
      setError('AD_Available_Time', {
        type: 'manual',
        message: 'hasSlotTakenError'
      })
      fetchSlots(getValues('AD_Date'))
    }
  }, [hasSlotTakenError, clearErrors, setError, getValues, fetchSlots])

  useEffect(() => {
    if (id && isOpen && !hasSlotTakenError) {
      setAvailabilities([])

      getBoutiqueAvailabilities({
        apiBaseUrl: canvasConfig.apiBaseUrl,
        id,
        language: canvasConfig.locale
      })
        .then(data => {
          setAvailabilities(
            isExternalPointOfSale
              ? data
              : data.filter(day =>
                  selectedPurpose.type === 'products' ? day?.salesSlotsCount > 0 : day?.serviceSlotsCount > 0
                )
          )
        })
        .catch(console.error)
    }
  }, [
    id,
    isOpen,
    canvasConfig.apiBaseUrl,
    canvasConfig.locale,
    isExternalPointOfSale,
    hasSlotTakenError,
    selectedPurpose.type
  ])

  useEffect(() => {
    if (id && isOpen && !appointmentDetails?.AD_Preferred_Language) {
      setSpokenLanguages([])
      getSpokenLanguages({
        apiBaseUrl: canvasConfig.apiBaseUrl,
        id,
        language: canvasConfig.locale
      })
        .then(data => {
          const langs = data.sort((a, b) => a.label.localeCompare(b.label))

          if (langs?.length === 1) {
            setValue('AD_Preferred_Language', langs[0].value)
            setSpokenLanguages(langs)
            return
          }

          const corresponding =
            langs.find(el => el.value === boutiqueLanguage) || langs.find(el => el.iso2Code === selectedCountry) || {}
          const rest = langs.filter(el => el.value !== corresponding?.value)

          if (corresponding) {
            setValue('AD_Preferred_Language', corresponding.value)
          }

          setSpokenLanguages([corresponding, ...rest])
        })
        .catch(console.error)

      if (comChannels) {
        setCommunicationChannels([...comChannels?.split(';')])
      }
    }
  }, [
    id,
    isOpen,
    selectedCountry,
    setValue,
    canvasConfig.apiBaseUrl,
    canvasConfig.locale,
    isExternalPointOfSale,
    comChannels,
    boutiqueLanguage,
    appointmentDetails
  ])

  useEffect(() => {
    if (id && isOpen && communicationChannels?.length === 1) {
      register('AD_Communication_Channel')
      setValue('AD_Communication_Channel', communicationChannels[0])
    }
  }, [id, isOpen, communicationChannels])

  const formatDateTime = useCallback(() => {
    const adDate =
      appointmentDetails.AD_Date &&
      dayjs(appointmentDetails.AD_Date).format(dayjs.locale() !== 'ar' ? 'L' : 'DD/MM/YYYY')
    const adAvailableTime =
      appointmentDetails.AD_Available_Time &&
      dayjs(new Date(new Date().toISOString().substring(0, 11) + appointmentDetails.AD_Available_Time)).format(
        dayjs.locale() !== 'ar' ? 'LT' : ', dddd'
      )
    const adPreferredTime =
      appointmentDetails.AD_Preferred_Time && t(`Appointment_Details.AD_${appointmentDetails.AD_Preferred_Time}`)

    let summaryText = ''

    if (adDate) {
      summaryText = adDate
    }

    if (adAvailableTime) {
      summaryText = summaryText + ' ' + adAvailableTime
      return summaryText
    }

    if (adPreferredTime) {
      summaryText = summaryText + ' ' + adPreferredTime
      return summaryText
    }
  }, [appointmentDetails.AD_Available_Time, appointmentDetails.AD_Date, appointmentDetails.AD_Preferred_Time, t])

  return (
    <Step
      isOpen={isOpen}
      isDone={isDone}
      isEnabled={isEnabled}
      isHighlighted={isHighlighted}
      onToggle={() => {
        isEnabled && onToggle(2)
      }}
      className={selectorClassName}
      index={2}
      title={t('Appointment_Details.AP_Appointment_Details')}
      summary={formatDateTime()}
      summaryExtra={
        getValues('AD_Appointment_Type') === 'INSTORE'
          ? t('Select_a_Boutique.SB_Appointment_Type_IS')
          : t('Select_a_Boutique.SB_Virtual_Appointment')
      }
    >
      <form
        className='rcc_c_form'
        onSubmit={handleSubmit(data => {
          dispatch({ type: 'go-to-3', payload: data })
          canvasConfig.onSectionExpanded({
            selectorClassName: 'rcc_c_card-step-information',
            index: 3
          })
          analytics.trackStep('baa_details')
        })}
      >
        <div className='rcc_c_form-content rcc-w-full'>
          <div className='rcc_c_form-group-input'>
            <Controller
              name='AD_Date'
              control={control}
              rules={{ required: true }}
              defaultValue={appointmentDetails.AD_Date || ''}
              shouldUnregister={true}
              render={({ field: { value } }) => (
                <InputDate
                  id='ad_date'
                  availabilities={availabilities}
                  hasError={errors.AD_Date}
                  label={
                    canvasConfig.locale === 'ar'
                      ? `* ${t('Appointment_Details.AD_Date')}`
                      : `${t('Appointment_Details.AD_Date')} *`
                  }
                  value={value}
                  direction={canvasConfig.direction}
                  placeholder={canvasConfig.locale === 'ar' ? t('Appointment_Details.AP_Choose_Date_Placeholder') : ''}
                  onChange={date => {
                    setValue('AD_Date', date)
                    if (!isExternalPointOfSale) {
                      fetchSlots(date)
                      setValue('AD_Available_Time', '')
                    }
                  }}
                />
              )}
            />
            <Controller
              name={isExternalPointOfSale ? 'AD_Preferred_Time' : 'AD_Available_Time'}
              control={control}
              rules={{ required: true }}
              shouldUnregister={true}
              defaultValue={
                (isExternalPointOfSale ? appointmentDetails.AD_Preferred_Time : appointmentDetails.AD_Available_Time) ||
                ''
              }
              render={({ field: { value } }) =>
                isExternalPointOfSale ? (
                  <Select
                    id='ad_preferred_time'
                    className='rcc-mt-9'
                    hasError={errors.AD_Preferred_Time}
                    label={
                      canvasConfig.locale === 'ar'
                        ? `* ${t('Appointment_Details.AD_PT')}`
                        : `${t('Appointment_Details.AD_PT')} *`
                    }
                    disabled={!watchADDate}
                    options={preferredTimes}
                    value={value}
                    onChange={e => setValue('AD_Preferred_Time', e.target.value)}
                    placeholder={
                      canvasConfig.locale === 'ar' ? t('Appointment_Details.AP_Set_RDV_Time_Placeholder') : null
                    }
                  />
                ) : (
                  <InputTime
                    id='ad_available_time'
                    className='rcc-mt-9'
                    key={value}
                    hasError={errors.AD_Available_Time}
                    errorLabel={t('Global.Slot_Taken_Error')}
                    label={
                      canvasConfig.locale === 'ar'
                        ? `* ${t('Appointment_Details.AD_Available_Time')}`
                        : `${t('Appointment_Details.AD_Available_Time')} *`
                    }
                    slots={slots}
                    date={getValues('AD_Date') || appointmentDetails.AD_Date || ''}
                    value={value}
                    onChange={slot => {
                      dispatch({ type: 'clear-slot-error' })
                      clearErrors('AD_Available_Time')
                      setValue('AD_Available_Time', slot)
                    }}
                    direction={canvasConfig.direction}
                    placeholder={
                      canvasConfig.locale === 'ar' ? t('Appointment_Details.AP_Set_RDV_Time_Placeholder') : null
                    }
                  />
                )
              }
            />
            <Controller
              name='AD_Preferred_Language'
              control={control}
              rules={{ required: true }}
              defaultValue={appointmentDetails.AD_Preferred_Language || ''}
              shouldUnregister={true}
              render={({ field: { value } }) => (
                <Select
                  id='ad_preferred_language'
                  className='rcc-mt-9'
                  hasError={errors.AD_Preferred_Language}
                  label={
                    canvasConfig.locale === 'ar'
                      ? `* ${t('Appointment_Details.AD_Preferred_Language')}`
                      : `${t('Appointment_Details.AD_Preferred_Language')} *`
                  }
                  options={spokenLanguages}
                  value={value}
                  placeholder={canvasConfig.locale === 'ar' && t('Appointment_Details.AP_Pick_Language_Placeholder')}
                  onChange={e => {
                    setValue('AD_Preferred_Language', e.target.value)
                  }}
                />
              )}
            />

            {isVirtualEnabled && enabledForVirtual && (
              <Controller
                name='AD_Appointment_Type'
                control={control}
                rules={{ required: true }}
                render={({ field: { value } }) => {
                  return (
                    <fieldset className='rcc-mt-9'>
                      <legend className='rcc-text-gray rcc-mb-2.5 rcc-p-0'>
                        {t('Select_a_Boutique.SB_Appointment_Type')}
                      </legend>
                      <div className='rcc-flex rcc-w-full rcc-gap-8'>
                        <InputCheckboxRadio
                          hasError={errors.AD_Appointment_Type}
                          checked={value === 'INSTORE'}
                          value='INSTORE'
                          onChange={() => setValue('AD_Appointment_Type', 'INSTORE')}
                          label={t('Select_a_Boutique.SB_Appointment_Type_IS')}
                          type='radio'
                          id={`rcc__inStore-${id}`}
                        />
                        <InputCheckboxRadio
                          hasError={errors.AD_Appointment_Type}
                          checked={value === 'VIRTUAL'}
                          value='VIRTUAL'
                          onChange={() => setValue('AD_Appointment_Type', 'VIRTUAL')}
                          label={t('Select_a_Boutique.SB_Appointment_Type_V')}
                          type='radio'
                          id={`rcc__virtual-${id}`}
                        />
                      </div>
                    </fieldset>
                  )
                }}
              />
            )}

            {getValues('AD_Appointment_Type') === 'VIRTUAL' && communicationChannels.length > 1 && (
              <Controller
                name='AD_Communication_Channel'
                control={control}
                rules={{ required: true }}
                defaultValue={appointmentDetails.AD_Communication_Channel || ''}
                shouldUnregister={true}
                render={() => (
                  <fieldset className='rcc-mt-9'>
                    <legend
                      className={clsx('rcc-text-gray rcc-mb-2.5 rcc-p-0', {
                        'rcc-text-danger': errors.AD_Communication_Channel,
                        'rcc-text-gray': !errors.AD_Communication_Channel
                      })}
                    >
                      {t('Appointment_Details.AD_Preferred_Virtual_Channel')}
                    </legend>
                    <div className='rcc_c_group-input-communication rcc-flex rcc-flex-wrap rcc-gap-x-8 rcc-gap-y-2'>
                      {communicationChannels?.map((el, i) => (
                        <InputCheckboxRadio
                          key={el}
                          checked={getValues('AD_Communication_Channel') === el}
                          onChange={() => setValue('AD_Communication_Channel', el)}
                          label={el}
                          type='radio'
                          id={`rcc_${el}`}
                        />
                      ))}
                    </div>
                  </fieldset>
                )}
              />
            )}

            {conf.configurations.displayComments && (
              <Controller
                name='AD_Comments'
                control={control}
                defaultValue={appointmentDetails.AD_Comments || ''}
                shouldUnregister={true}
                render={({ field: { value } }) => {
                  const additionalLine = t('Appointment_Details.AD_Comments_Additional_Line')
                  const hasAdditionalLine =
                    !additionalLine.includes('Appointment_Details') && canvasConfig.locale !== 'ar'
                  const placeholderLabel = `${t('Appointment_Details.AD_Comments')}\n${
                    hasAdditionalLine ? t('Appointment_Details.AD_Comments_Additional_Line') : ''
                  }`

                  return (
                    <Textarea
                      id='ad_complementary_comments'
                      label={t('Appointment_Details.AD_Complementary_Comments')}
                      placeholder={placeholderLabel}
                      ariaPlaceholder={placeholderLabel}
                      value={value}
                      onChange={e => {
                        setValue('AD_Comments', e.target.value)
                      }}
                    />
                  )
                }}
              />
            )}
          </div>
        </div>
        <Button type='submit' className='rcc_c_form-btn-submit'>
          {t('Global.Confirm')}
        </Button>
      </form>
    </Step>
  )
}

export default CardStepDetails
